import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axiosInstance";

function formatDate(dateValue) {
  if (!dateValue) return "Not applied yet";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "Invalid date";
  return parsed.toLocaleString();
}

function countRoomImages(roomImages) {
  if (!roomImages || typeof roomImages !== "object") return 0;
  return Object.values(roomImages).reduce(
    (total, rows) => total + (Array.isArray(rows) ? rows.length : 0),
    0
  );
}

function profileLines(profile) {
  const source = profile || {};
  return [
    source.public_hotel_name,
    [source.address_line, source.postal_code, source.city].filter(Boolean).join(", "),
    source.country,
    Array.isArray(source.phones) && source.phones.length ? `Phones: ${source.phones.join(", ")}` : "",
    Array.isArray(source.emails) && source.emails.length ? `Emails: ${source.emails.join(", ")}` : "",
  ].filter(Boolean);
}

export default function OnboardingBookingEngineAdmin() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [applyingBusinessUnitId, setApplyingBusinessUnitId] = useState(null);
  const [feedback, setFeedback] = useState("");

  const selectedCopyRows = useMemo(
    () => (Array.isArray(selectedDraft?.room_category_copy) ? selectedDraft.room_category_copy : []),
    [selectedDraft?.room_category_copy]
  );
  const selectedImageCount = useMemo(
    () => countRoomImages(selectedDraft?.room_category_images) + (selectedDraft?.hero_asset ? 1 : 0),
    [selectedDraft?.hero_asset, selectedDraft?.room_category_images]
  );
  const selectedAmenityCount = useMemo(
    () =>
      selectedCopyRows.reduce(
        (total, row) => total + (Array.isArray(row.amenity_ids) ? row.amenity_ids.length : 0),
        0
      ),
    [selectedCopyRows]
  );

  const loadDrafts = async () => {
    setLoading(true);
    setFeedback("");

    try {
      const response = await axios.get("/api/onboarding/admin/booking-engine/drafts");
      setDrafts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("[Admin][Onboarding][BookingEngine] Error loading drafts:", error);
      setFeedback("Could not load Booking Engine drafts.");
    } finally {
      setLoading(false);
    }
  };

  const loadDraftDetail = async (businessUnitId) => {
    setSelectedBusinessUnitId(businessUnitId);
    setDetailLoading(true);
    setFeedback("");

    try {
      const response = await axios.get(`/api/onboarding/admin/booking-engine/${businessUnitId}`);
      setSelectedDraft(response.data);
    } catch (error) {
      console.error("[Admin][Onboarding][BookingEngine] Error loading draft detail:", error);
      setSelectedDraft(null);
      setFeedback("Could not load the selected Booking Engine draft.");
    } finally {
      setDetailLoading(false);
    }
  };

  const applyDraft = async (businessUnitId, businessUnitName) => {
    const confirmed = window.confirm(
      `Apply the onboarding Booking Engine draft for ${businessUnitName}? This will publish settings, profile, copy, amenities and images to the live Booking Engine tables.`
    );

    if (!confirmed) return;

    setApplyingBusinessUnitId(businessUnitId);
    setFeedback("");

    try {
      const response = await axios.post(`/api/onboarding/admin/booking-engine/${businessUnitId}/apply`);
      const result = response.data?.result || {};
      setFeedback(
        `Booking Engine draft applied for ${businessUnitName}. Copy created: ${result.room_copy_created || 0}. Copy updated: ${result.room_copy_updated || 0}. Assets created: ${result.assets_created || 0}. Assets updated: ${result.assets_updated || 0}.`
      );
      await loadDrafts();
      await loadDraftDetail(businessUnitId);
    } catch (error) {
      console.error("[Admin][Onboarding][BookingEngine] Error applying draft:", error);
      setFeedback(error?.response?.data?.error || "Could not apply the selected Booking Engine draft.");
    } finally {
      setApplyingBusinessUnitId(null);
    }
  };

  useEffect(() => {
    loadDrafts();
  }, []);

  return (
    <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "18px" }}>
      <div
        style={{
          borderRadius: "22px",
          padding: "22px",
          border: "1px solid rgba(255,255,255,0.08)",
          background:
            "radial-gradient(780px 240px at 0% 0%, rgba(92,191,255,0.12), transparent 60%), rgba(10,15,28,0.9)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
        }}
      >
        <div style={{ fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.12em", color: "#8ea0bc" }}>
          Admin apply area
        </div>
        <h2 style={{ color: "#fff", margin: "8px 0 10px" }}>Onboarding Booking Engine</h2>
        <p style={{ color: "#b9c3d7", maxWidth: "860px", margin: 0, lineHeight: 1.6 }}>
          Review the staged direct-booking setup and apply it to the live Booking Engine only when the hotel is ready.
        </p>
      </div>

      {feedback ? (
        <div
          style={{
            borderRadius: "14px",
            padding: "12px 14px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: feedback.toLowerCase().includes("could not") || feedback.toLowerCase().includes("must")
              ? "#542929"
              : "#244428",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {feedback}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(280px, 0.9fr) minmax(0, 1.4fr)",
          gap: "18px",
          alignItems: "start",
        }}
      >
        <section
          style={{
            borderRadius: "18px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(10,15,28,0.82)",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "16px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <h3 style={{ color: "#fff", margin: 0 }}>Drafts</h3>
          </div>

          {loading ? (
            <div style={{ padding: "18px", color: "#9db0cf" }}>Loading drafts...</div>
          ) : drafts.length === 0 ? (
            <div style={{ padding: "18px", color: "#9db0cf" }}>No Booking Engine drafts yet.</div>
          ) : (
            drafts.map((draft) => {
              const isSelected = Number(selectedBusinessUnitId) === Number(draft.business_unit_id);
              return (
                <button
                  type="button"
                  key={`booking-engine-draft-${draft.session_id}`}
                  onClick={() => loadDraftDetail(draft.business_unit_id)}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    border: "none",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    padding: "16px 18px",
                    background: isSelected ? "rgba(92,191,255,0.12)" : "transparent",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{draft.business_unit_name}</div>
                  <div style={{ color: "#9db0cf", marginTop: "6px", fontSize: "0.9rem" }}>
                    {draft.booking_engine_slug || "No slug"} · {draft.rows_total || 0} copy rows ·{" "}
                    {draft.assets_total || 0} assets
                  </div>
                  <div style={{ color: "#6f83a5", marginTop: "4px", fontSize: "0.82rem" }}>
                    Updated {formatDate(draft.updated_at)}
                  </div>
                </button>
              );
            })
          )}
        </section>

        <section
          style={{
            borderRadius: "18px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(10,15,28,0.82)",
            minHeight: "420px",
            padding: "18px",
          }}
        >
          {detailLoading ? (
            <div style={{ color: "#9db0cf" }}>Loading draft detail...</div>
          ) : !selectedDraft ? (
            <div style={{ color: "#9db0cf" }}>Select a draft to preview its staged Booking Engine setup.</div>
          ) : (
            <div style={{ display: "grid", gap: "18px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "14px", flexWrap: "wrap" }}>
                <div>
                  <div style={{ color: "#8ea0bc", fontSize: "0.78rem", textTransform: "uppercase" }}>
                    {selectedDraft.session?.business_unit_name}
                  </div>
                  <h3 style={{ color: "#fff", margin: "6px 0" }}>
                    {selectedDraft.draft?.booking_engine_slug || "Booking Engine draft"}
                  </h3>
                  <div style={{ color: "#9db0cf" }}>
                    Applied: {formatDate(selectedDraft.session?.applied_at)}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    applyDraft(selectedDraft.session.business_unit_id, selectedDraft.session.business_unit_name)
                  }
                  disabled={applyingBusinessUnitId === selectedDraft.session.business_unit_id}
                  style={{
                    border: "none",
                    borderRadius: "14px",
                    padding: "12px 16px",
                    background: "#5cbfff",
                    color: "#08111e",
                    fontWeight: 800,
                    cursor: "pointer",
                    opacity: applyingBusinessUnitId === selectedDraft.session.business_unit_id ? 0.7 : 1,
                  }}
                >
                  {applyingBusinessUnitId === selectedDraft.session.business_unit_id ? "Applying..." : "Apply draft"}
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "12px" }}>
                {[
                  ["Room copy", selectedCopyRows.length],
                  ["Amenities", selectedAmenityCount],
                  ["Images", selectedImageCount],
                  ["Terms", selectedDraft.draft?.booking_engine_terms?.length || 0],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      borderRadius: "14px",
                      border: "1px solid rgba(255,255,255,0.08)",
                      background: "rgba(255,255,255,0.03)",
                      padding: "14px",
                    }}
                  >
                    <div style={{ color: "#8ea0bc", fontSize: "0.82rem" }}>{label}</div>
                    <div style={{ color: "#fff", fontSize: "1.5rem", fontWeight: 800, marginTop: "6px" }}>{value}</div>
                  </div>
                ))}
              </div>

              <div
                style={{
                  borderRadius: "14px",
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.03)",
                  padding: "14px",
                }}
              >
                <h4 style={{ color: "#fff", margin: "0 0 10px" }}>Public profile</h4>
                {profileLines(selectedDraft.profile).length ? (
                  profileLines(selectedDraft.profile).map((line) => (
                    <div key={line} style={{ color: "#c7d2e7", marginTop: "5px" }}>
                      {line}
                    </div>
                  ))
                ) : (
                  <div style={{ color: "#9db0cf" }}>No public profile data staged.</div>
                )}
              </div>

              <div style={{ display: "grid", gap: "10px" }}>
                <h4 style={{ color: "#fff", margin: 0 }}>Room category copy</h4>
                {selectedCopyRows.length ? (
                  selectedCopyRows.map((row) => (
                    <div
                      key={`copy-${row.id || row.onboarding_room_category_id}`}
                      style={{
                        borderRadius: "14px",
                        border: "1px solid rgba(255,255,255,0.08)",
                        background: "rgba(255,255,255,0.03)",
                        padding: "14px",
                      }}
                    >
                      <div style={{ color: "#fff", fontWeight: 800 }}>
                        {row.name_en} / {row.name_es}
                      </div>
                      <div style={{ color: "#9db0cf", marginTop: "6px" }}>
                        Staged category #{row.onboarding_room_category_id}
                        {row.applied_booking_engine_room_category_id
                          ? ` · live copy #${row.applied_booking_engine_room_category_id}`
                          : " · not applied yet"}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: "#9db0cf" }}>No room category copy staged.</div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
